import { ItiHttpService } from "@/iti-core-react/services/iti-http-service";
import { AutomationDto } from "@/shared/dtos/automation-dto";
import { API } from "../api";
import { FileTypeDto } from "../dtos/file-type-dto";
import { StreamingTechnologyDto } from "../dtos/streaming-technology-dto";
import { TaskTypeDto } from "../dtos/task-type-dto";
import { TechnologyDto } from "../dtos/technologies-dto";
import { SortDirection } from "../enums/catalogue-search-filter-type";
import { StreamingSortByEnum } from "../enums/streaming-sort";

export const AutomationService = {
	async getAutomations(params?: {
		sessionName?: string;
		statuses?: string[]; // ["PEN","INI","PAU","FIN","DEL","EQ1","EQ2","EP1"]
		taskTypeId?: number[] | null;
		technologyId?: number[] | null;
		page?: number;
		perPage?: number;
		sortDirection?: SortDirection;
		sortBy?: StreamingSortByEnum;
	}): Promise<{
		content: AutomationDto[];
		totalElements: number;
		totalPages: number;
		currentPage: number;
	}> {
		const page = params?.page ?? 0;
		const perPage = params?.perPage ?? 12;

		const availableTaskTypes = await AutomationService.getTaskTypes(true);
		const availableTaskTypeIds = availableTaskTypes.map((tt) => tt.id);
		let taskTypeIds: number[] = [];
		if (
			params?.taskTypeId &&
			Array.isArray(params.taskTypeId) &&
			params.taskTypeId.length > 0
		) {
			taskTypeIds = params.taskTypeId.map((id) => Number(id));
		} else {
			taskTypeIds = availableTaskTypeIds;
		}

		let technologyIds: number[] | undefined = undefined;
		if (
			params?.technologyId &&
			Array.isArray(params.technologyId) &&
			params.technologyId.length > 0
		) {
			technologyIds = params.technologyId.map((id) => Number(id));
		}

		const payload: any = {
			sessionName: params?.sessionName || undefined,
			status: params?.statuses?.length ? params.statuses : undefined,
			taskTypeIds: taskTypeIds.length > 0 ? taskTypeIds : undefined,
			technologyIds: technologyIds,
			limit: perPage,
			offset: page * perPage,
			sortBy: params?.sortBy ?? StreamingSortByEnum.STREAMING_NAME,
			sort: params?.sortDirection === SortDirection.DESC ? "DESC" : "ASC",
		};

		for (const k of Object.keys(payload)) {
			if (payload[k] === undefined) {
				delete payload[k];
			}
		}

		const res = await ItiHttpService.post(
			`${API.streaming.base}/search`,
			payload,
			false,
		);

		const raw = Array.isArray(res?.content)
			? res.content
			: Array.isArray(res?.streaming)
				? res.streaming
				: [];

		const total =
			typeof res?.totalStreaming === "number"
				? res.totalStreaming
				: typeof res?.totalElements === "number"
					? res.totalElements
					: raw.length;
		return {
			content: raw.map((it: any) => new AutomationDto(it)),
			totalElements: total,
			totalPages: Math.max(1, Math.ceil(total / perPage)),
			currentPage: page,
		};
	},

	async getTaskTypes(ingestionTaskType?: boolean): Promise<TaskTypeDto[]> {
		const query =
			ingestionTaskType === undefined ? undefined : { ingestionTaskType };

		return ItiHttpService.get(API.streaming.taskTypes, query);
	},

	async getTechnologiesByTaskType(
		taskTypeId: number,
	): Promise<StreamingTechnologyDto[]> {
		const res = await ItiHttpService.get(
			`${API.streaming.technologiesTaskTypes}/${taskTypeId}`,
		);
		return res.map(
			(item: any) =>
				new StreamingTechnologyDto({
					technologyId: item.technologyId,
					technologyName: item.technologyName,
					taskTypeId: item.taskTypeId,
					taskTypeDesc: item.taskTypeDesc,
				}),
		);
	},

	async getAutomation(id: string): Promise<AutomationDto> {
		const res = await ItiHttpService.get(`${API.streaming.base}/${id}`);
		return new AutomationDto(res);
	},
	async delete(id: string): Promise<boolean> {
		return await ItiHttpService.delete(`${API.streaming.base}/${id}`);
	},
	async create(
		automation: AutomationDto,
		showLoading = true,
	): Promise<AutomationDto> {
		return await ItiHttpService.post(
			`${API.streaming.base}/connector`,
			automation.toJson(),
			showLoading,
		);
	},
	async update(
		automation: AutomationDto,
		sessionId: string,
	): Promise<AutomationDto> {
		return await ItiHttpService.put(
			`${API.streaming.base}/connector/${sessionId}`,
			automation.toJson(),
		);
	},
	async getTechnologies(ingestionTaskType?: boolean): Promise<TechnologyDto[]> {
		const query =
			ingestionTaskType === undefined ? undefined : { ingestionTaskType };
		const res = await ItiHttpService.get(API.streaming.technologies, query);
		return res.map((item: TechnologyDto) => new TechnologyDto(item));
	},
	async getStreamingTechnologies(): Promise<StreamingTechnologyDto[]> {
		const res = await ItiHttpService.get(
			`${API.streaming.technologiesTaskTypes}/1`,
		);

		return res.map(
			(item: any) =>
				new StreamingTechnologyDto({
					technologyId: item.technologyId,
					technologyName: item.technologyName,
					taskTypeId: item.taskTypeId,
					taskTypeDesc: item.taskTypeDesc,
				}),
		);
	},

	async getfileTypes(): Promise<FileTypeDto[]> {
		const res = await ItiHttpService.get(`${API.streaming.filetypes}`);
		return res.map((item: FileTypeDto) => new FileTypeDto(item));
	},
	async startAutomation(id: string): Promise<string> {
		const res = await ItiHttpService.post(
			`${API.streaming.base}/${id}/start`,
			{},
			false,
		);
		return res;
	},
	async pauseAutomation(id: string): Promise<string> {
		const res = await ItiHttpService.post(
			`${API.streaming.base}/${id}/pause`,
			{},
			false,
		);
		return res;
	},
	async stopAutomation(id: string): Promise<string> {
		const res = await ItiHttpService.post(
			`${API.streaming.base}/${id}/finish`,
			{},
			false,
		);
		return res;
	},
};
